import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* 變體對應舊版 styles.css 的 .btn-primary / .btn-secondary / .btn-ghost …
   min-h-[44px] 是全站觸控標準，不要調小。
   按下時縮 1.5% 並下沉 1px（120ms），是全站一致的「按到了」回饋；
   disabled 已關掉 pointer-events，不會誤播。 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-semibold ' +
    'transition-[color,background-color,border-color,transform,box-shadow] duration-[120ms] ' +
    'active:translate-y-px active:scale-[0.985] ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
    'focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-[#7F1D1D]',
        secondary: 'border border-border bg-card text-foreground hover:border-muted-foreground',
        ghost: 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-95',
        dangerSoft: 'bg-accent text-accent-foreground hover:bg-[#fecaca]',
        success: 'bg-success text-white hover:brightness-95',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-[44px] px-4 py-2.5',
        /* 36px 在滑鼠下夠用，但觸控裝置上低於 44px 的建議值。
           用 pointer:coarse 只在觸控環境放大，桌面維持原本的密度。 */
        sm: 'min-h-[36px] px-3 text-[13px] max-md:min-h-[44px] [@media(pointer:coarse)]:min-h-[44px]',
        lg: 'min-h-[48px] px-6',
        icon: 'min-h-[44px] w-11',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'default', size: 'default', block: false },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, block }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
